/**
 * DERAG12_36R2Service — Geocodificador L1.x para organizaciones humanitarias
 *
 * Busca organizaciones humanitarias DERA G12 (capa `g12_36_OrganizacionesHumanitarias`,
 * 107 features cross-provincia: 97 Cruz Roja + 10 Cáritas) almacenadas en R2
 * mediante fuzzy matching por nombre (sede/asamblea o tipo+municipio).
 *
 * Datos: DERA G12 servicios — Decreto 197/2024 · Atención humanitaria emergencias
 * Volumen: 107 features (verif. empírica 2026-05-21 WFS GetFeature numberMatched)
 * Cobertura: 91 INEs cross-provincia (verif. empírica build productivo 21-may)
 * CRS: EPSG:25830 (UTM30 ETRS89 — nativo, sin reproyección)
 * Ruta R2: `dera/humanitarias.json.gz` (global, DOP-RE-2=α simetría WAVE-1)
 * Precisión: ±10 m (coordenadas oficiales DERA G12)
 *
 * Patrón arquitectónico: réplica DERAG12_38R2Service (DOP-RE-3=α · sesión
 * `WAVE2-R2-EXPORT` 21-may eslabón 37 cadena HX-COMMIT-ATOMIC arco F2).
 * El service hace 1 fetch global cacheado + filtra in-memory por codINE.
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-05-21
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  DeraG12_36Feature,
  DeraG12_36SearchOptions,
  DeraG12_36Result,
} from '../../../types/deraG12_36';
import {
  G12_36_X_RANGE,
  G12_36_Y_RANGE,
  G12_36_PRECISION_M,
} from '../../../types/deraG12_36';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/** URL base del Worker R2 (fuente única de verdad: `workerEndpoints.WORKER_BASE`). */
const WORKER_BASE_URL = WORKER_BASE;

/** Ruta global del dataset humanitarias (DOP-RE-2=α simetría WAVE-1). */
const R2_PATH = '/v1/dera/humanitarias';

/** Timeout fetch (ms). */
const FETCH_TIMEOUT = 15_000;

/** TTL de la caché global (24 h alineado con `Cache-Control: max-age=86400`). */
const CACHE_TTL_MS = 24 * 60 * 60 * 1_000;

/** Configuración uFuzzy para nombres españoles (idéntico a DERAG3R2Service). */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// ERROR TIPADO DE CARGA
// =============================================================================

/**
 * Error de carga del dataset global G12_36 desde R2.
 *
 * `DOP-GSD1-DESTINO`=(a) · sesión `GSD1` 04-ago. Antes, cualquier fallo de
 * carga (404, HTTP no-ok, red, timeout, cuerpo con forma inválida) devolvía
 * `[]` y el geocodificador daba **cero candidatos en verde**: el PTEL salía
 * sin estas infraestructuras sin que nada avisara (`HX-FALLBACK-ENMASCARA-BUG`).
 *
 * Ahora la carga propaga este error para que el consumidor distinga «cero
 * resultados» de «fallo de carga». El orquestador ya lo maneja: su `.catch()`
 * registra `circuitBreaker.recordFailure('DERA_G12_36_R2')` y degrada la
 * fuente de forma explícita.
 */
export class DeraG12_36LoadError extends Error {
  constructor(
    message: string,
    public readonly detail?: unknown,
  ) {
    super(`[DERAG12_36] ${message}`);
    this.name = 'DeraG12_36LoadError';
  }
}

/** Describe la forma recibida para que el mensaje de error sea diagnosticable. */
function describeShape(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') {
    const keys = Object.keys(value as object);
    const shown = keys.slice(0, 5).join(', ');
    return `objeto con claves [${shown}${keys.length > 5 ? ', …' : ''}]`;
  }
  return typeof value;
}

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: DERAG12_36R2Service | null = null;

/** Devuelve la instancia singleton del service (lazy). */
export function getDERAG12_36R2Service(): DERAG12_36R2Service {
  if (!serviceInstance) {
    serviceInstance = new DERAG12_36R2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests). */
export function resetDERAG12_36R2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAG12_36R2Service {
  private readonly baseUrl: string;
  private readonly fuzzy: uFuzzy;

  /** Caché global (1 sola entrada por ser dataset único). */
  private cache: { data: DeraG12_36Feature[]; ts: number } | null = null;

  /** Promesa en vuelo para deduplicar fetches concurrentes. */
  private loadingPromise: Promise<DeraG12_36Feature[]> | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS (global)
  // ---------------------------------------------------------------------------

  /**
   * Carga el dataset global humanitarias desde R2 (con caché TTL).
   * Llamadas concurrentes comparten la misma promesa en vuelo.
   */
  async loadAll(): Promise<DeraG12_36Feature[]> {
    if (this.cache && Date.now() - this.cache.ts < CACHE_TTL_MS) {
      return this.cache.data;
    }

    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = this.fetchAll();
    try {
      const features = await this.loadingPromise;
      this.cache = { data: features, ts: Date.now() };
      return features;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Carga el dataset global desde R2.
   *
   * `DOP-GSD1-DESTINO`=(a): **ningún camino de fallo devuelve `[]`**. Las
   * cuatro puertas que antes producían un vacío silencioso —404, HTTP no-ok,
   * error de red o timeout, y cuerpo con forma inválida— propagan ahora
   * `DeraG12_36LoadError`. Un `[]` que salga de aquí significa exactamente
   * una cosa: R2 respondió un array y ninguna feature superó `isValidCoord`.
   *
   * El `try` envuelve **solo** la llamada de red, para que el propio `catch`
   * no se trague los errores que este método emite a propósito.
   */
  private async fetchAll(): Promise<DeraG12_36Feature[]> {
    const url = `${this.baseUrl}${R2_PATH}`;

    let response: Response;
    try {
      ({ response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT }));
    } catch (error) {
      const esTimeout = (error as Error)?.name === 'AbortError';
      throw new DeraG12_36LoadError(
        esTimeout
          ? `timeout cargando el dataset global desde ${url}`
          : `error de red cargando el dataset global desde ${url}`,
        error,
      );
    }

    if (!response.ok) {
      throw new DeraG12_36LoadError(
        `R2 respondió HTTP ${response.status} para ${url}`,
      );
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch (error) {
      throw new DeraG12_36LoadError(
        `el cuerpo de ${url} no es JSON válido`,
        error,
      );
    }

    if (!Array.isArray(data)) {
      throw new DeraG12_36LoadError(
        `el cuerpo de ${url} no es un array (recibido: ${describeShape(data)})`,
        data,
      );
    }

    return (data as DeraG12_36Feature[]).filter((f) => this.isValidCoord(f));
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica una organización humanitaria.
   * Devuelve el mejor match o `null` si no hay candidatos sobre el umbral.
   */
  async geocode(
    options: DeraG12_36SearchOptions,
  ): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name);
  }

  /**
   * Búsqueda completa: filtra por codINE → fuzzy match por nombre →
   * aplica umbral y maxResults.
   */
  async search(options: DeraG12_36SearchOptions): Promise<DeraG12_36Result[]> {
    const { codINE, name, minConfidence = 60, maxResults = 5 } = options;
    if (!/^\d{5}$/.test(codINE)) return [];

    const all = await this.loadAll();
    if (all.length === 0) return [];

    const candidates = all.filter((f) => f.cod_mun === codINE);
    if (candidates.length === 0) return [];

    if (!name?.trim()) {
      return candidates.slice(0, maxResults).map((f) => this.toResult(f, 70));
    }

    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING (idéntico a DERAG12_38R2Service)
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: DeraG12_36Feature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): DeraG12_36Result[] {
    const haystack = candidates.map((f) => f.name);
    const normalizedName = searchName.toLowerCase().trim();
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: DeraG12_36Result[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature.name);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  private calcFuzzyScore(needle: string, haystack: string): number {
    const hLow = haystack.toLowerCase();
    if (hLow === needle) return 1.0;
    if (hLow.includes(needle)) return 0.95;
    if (needle.includes(hLow)) return 0.9;
    const ratio = needle.length / Math.max(hLow.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 95;
    if (score >= 0.85) return 88;
    if (score >= 0.7) return 78;
    if (score >= 0.55) return 68;
    if (score >= 0.4) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN A RESULTADO
  // ---------------------------------------------------------------------------

  private toResult(
    feature: DeraG12_36Feature,
    confidence: number,
    fuzzyScore?: number,
  ): DeraG12_36Result {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: G12_36_PRECISION_M,
      source: 'dera_g12_36_r2',
      feature,
      fuzzyScore,
    };
  }

  toGeocodingResult(
    result: DeraG12_36Result,
    originalQuery?: string,
  ): GeocodingResult {
    const f = result.feature;
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'dera_g12_36_r2',
      sourceLayer: f.source_layer,
      originalQuery,
      matchedName: f.name,
      matchType: result.fuzzyScore != null ? 'fuzzy' : 'filter',
      fuzzyScore: result.fuzzyScore,
      municipality: f.municipio,
      muniCode: f.cod_mun,
      province: f.provincia,
      // g12_36 expone `direccion` postal completa (a diferencia de g12_37/g12_38).
      address: f.direccion ?? undefined,
      metadata: {
        id_dera: f.id_dera,
        nombre: f.nombre ?? '',
        tipo: f.tipo ?? '',
        direccion: f.direccion ?? '',
        tipologiaPTEL: f.tipologiaPTEL,
        tipoOriginal: f.tipoOriginal,
        decretoReferencia: f.decretoReferencia,
        contextoEmergencia: f.contextoEmergencia,
        precision: result.precision,
        fuente: 'DERA G12 Organizaciones Humanitarias',
        schema_version: f.schema_version,
        fetched_at: f.fetched_at,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private isValidCoord(f: DeraG12_36Feature): boolean {
    return (
      f.x >= G12_36_X_RANGE.min &&
      f.x <= G12_36_X_RANGE.max &&
      f.y >= G12_36_Y_RANGE.min &&
      f.y <= G12_36_Y_RANGE.max
    );
  }

  clearCache(): void {
    this.cache = null;
    this.loadingPromise = null;
  }

  /** Carga datos directamente (para tests sin fetch). */
  loadFromData(features: DeraG12_36Feature[]): void {
    const valid = features.filter((f) => this.isValidCoord(f));
    this.cache = { data: valid, ts: Date.now() };
  }

  /** Tamaño actual del dataset cacheado (0 si no cargado). */
  get cachedFeatureCount(): number {
    return this.cache?.data.length ?? 0;
  }
}

export default DERAG12_36R2Service;
